import React, { useState, useEffect } from 'react';
import { agencyAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Building2, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';

export const Agencies = () => {
  const [agencies, setAgencies] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showAgencyDialog, setShowAgencyDialog] = useState(false);
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [agencyForm, setAgencyForm] = useState({ name: '', city: '', region: '', director: '' });
  const [agentForm, setAgentForm] = useState({ name: '', agency_id: '', email: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [agenciesRes, agentsRes] = await Promise.all([
        agencyAPI.getAgencies(),
        agencyAPI.getAgents()
      ]);
      setAgencies(agenciesRes.data);
      setAgents(agentsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgency = async (e) => {
    e.preventDefault();
    try {
      await agencyAPI.createAgency(agencyForm);
      toast.success('Agence créée avec succès!');
      setShowAgencyDialog(false);
      fetchData();
      setAgencyForm({ name: '', city: '', region: '', director: '' });
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    try {
      await agencyAPI.createAgent(agentForm);
      toast.success('Agent créé avec succès!');
      setShowAgentDialog(false);
      fetchData();
      setAgentForm({ name: '', agency_id: '', email: '' });
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const getAgencyAgents = (agencyId) => {
    return agents.filter(a => a.agency_id === agencyId);
  };

  return (
    <div className="p-8 space-y-8" data-testid="agencies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gestion des Agences
          </h1>
          <p className="text-muted-foreground mt-2">Agences et agents commerciaux</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showAgencyDialog} onOpenChange={setShowAgencyDialog}>
            <DialogTrigger asChild>
              <Button data-testid="new-agency-button">
                <Building2 size={18} className="mr-2" />
                Nouvelle Agence
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Créer une Agence</DialogTitle>
                <DialogDescription>Ajouter une nouvelle agence d'assurance</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateAgency} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="agency-name">Nom de l'Agence</Label>
                  <Input
                    id="agency-name"
                    data-testid="agency-name-input"
                    value={agencyForm.name}
                    onChange={(e) => setAgencyForm({ ...agencyForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="agency-city">Ville</Label>
                  <Input
                    id="agency-city"
                    data-testid="agency-city-input"
                    value={agencyForm.city}
                    onChange={(e) => setAgencyForm({ ...agencyForm, city: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="agency-region">Région</Label>
                  <Input
                    id="agency-region"
                    data-testid="agency-region-input"
                    value={agencyForm.region}
                    onChange={(e) => setAgencyForm({ ...agencyForm, region: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="agency-director">Directeur</Label>
                  <Input
                    id="agency-director"
                    data-testid="agency-director-input"
                    value={agencyForm.director}
                    onChange={(e) => setAgencyForm({ ...agencyForm, director: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="agency-submit-button">
                  Créer
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showAgentDialog} onOpenChange={setShowAgentDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="new-agent-button">
                <UserPlus size={18} className="mr-2" />
                Nouvel Agent
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Créer un Agent</DialogTitle>
                <DialogDescription>Ajouter un nouvel agent commercial</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateAgent} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="agent-name">Nom de l'Agent</Label>
                  <Input
                    id="agent-name"
                    data-testid="agent-name-input"
                    value={agentForm.name}
                    onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="agent-email">Email</Label>
                  <Input
                    id="agent-email"
                    data-testid="agent-email-input"
                    type="email"
                    value={agentForm.email}
                    onChange={(e) => setAgentForm({ ...agentForm, email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="agent-agency">Agence</Label>
                  <select
                    id="agent-agency"
                    data-testid="agent-agency-select"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={agentForm.agency_id}
                    onChange={(e) => setAgentForm({ ...agentForm, agency_id: e.target.value })}
                    required
                  >
                    <option value="">Sélectionner une agence</option>
                    {agencies.map(agency => (
                      <option key={agency.id} value={agency.id}>{agency.name} - {agency.city}</option>
                    ))}
                  </select>
                </div>
                <Button type="submit" className="w-full" data-testid="agent-submit-button">
                  Créer
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Agencies Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {loading ? (
          <div className="col-span-full text-center py-8 text-muted-foreground">Chargement...</div>
        ) : agencies.length > 0 ? (
          agencies.map((agency, index) => {
            const agencyAgents = getAgencyAgents(agency.id);
            return (
              <Card key={agency.id} data-testid={`agency-card-${index}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl">{agency.name}</CardTitle>
                      <CardDescription>{agency.city}, {agency.region}</CardDescription>
                    </div>
                    <Building2 className="text-primary" size={32} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                    <span className="text-sm text-muted-foreground">Directeur</span>
                    <span className="font-semibold text-sm">{agency.director}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                    <span className="text-sm text-muted-foreground flex items-center gap-2">
                      <Users size={16} />
                      Agents
                    </span>
                    <span className="font-bold data-cell">{agencyAgents.length}</span>
                  </div>
                  {agencyAgents.length > 0 && (
                    <div className="pt-2 border-t border-border/60">
                      <p className="text-xs text-muted-foreground mb-2">Agents attachés:</p>
                      <div className="space-y-1">
                        {agencyAgents.slice(0, 3).map(agent => (
                          <div key={agent.id} className="text-sm flex items-center justify-between">
                            <span>{agent.name}</span>
                            <span className="text-xs text-muted-foreground">{agent.email}</span>
                          </div>
                        ))}
                        {agencyAgents.length > 3 && (
                          <p className="text-xs text-muted-foreground">+{agencyAgents.length - 3} autres</p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        ) : (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            <Building2 size={48} className="mx-auto mb-4 opacity-50" />
            <p>Aucune agence créée</p>
            <p className="text-sm mt-2">Créez votre première agence pour commencer</p>
          </div>
        )}
      </div>
    </div>
  );
};